import { notify } from './notify';
import { showErrorModal } from './errorModalEmitter';

/**
 * Global error handler for API errors across all call patterns:
 * - Factory hooks (createEntityQuery/createEntityMutation) via globalErrorHandler
 * - notify.promise() catch block
 * - ConfirmDialog onAction catch via confirmErrorHandler
 * - safeApiCall() wrapper
 *
 * Behavior aligned with legacy useWealthlaneAwardApi.handleRequest toasts.
 *
 * Silently ignores:
 * - 401 (auth interceptor handles token refresh)
 * - 403 from GET requests (interceptor fires PERMISSION_CHANGED → page redirect)
 *
 * Shared across all apps (customer, participant, etc.)
 */
const SUPPRESSED_PATTERNS = [
  'OTP is invalid',
  'Invalid Template',
  'Only excel file can be uploaded',
  'Index was outside the bounds of the array.',
];

/** Same error object must not toast twice (e.g. handleRequest + useTableSave). */
const toastedErrors = new WeakSet<object>();

function isSuppressed(msg: string): boolean {
  return SUPPRESSED_PATTERNS.some((pattern) => msg.includes(pattern));
}

function splitErrorLines(message: string): string[] {
  return message
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/** Legacy handleRequest: one toast per line; skip lines matching suppressed patterns. */
function toastErrorLines(lines: string[]): boolean {
  const toShow = lines.filter((line) => !isSuppressed(line));
  if (toShow.length === 0) return false;
  toShow.forEach((line) => {
    notify.error(line, { autoClose: 30000 });
  });
  return true;
}

function alreadyToasted(error: unknown): boolean {
  if (error == null || (typeof error !== 'object' && typeof error !== 'function')) {
    return false;
  }
  if (toastedErrors.has(error)) {
    return true;
  }
  toastedErrors.add(error);
  return false;
}

export function showApiErrorToast(error: unknown): void {
  if (alreadyToasted(error)) {
    return;
  }

  const raw = error as Record<string, any>;

  const status: number | undefined = raw?.status ?? raw?.response?.status;
  const errorPayload: { message?: string; validationErrors?: Array<{ message: string }> } | undefined =
    raw?.body?.error ?? raw?.response?.data?.error;

  if (status === 401) return;

  if (status === 403) {
    const method = (raw?.config?.method ?? raw?.request?.method ?? '').toLowerCase();
    const isGetRequest = method === '' || method === 'get';
    if (isGetRequest) return;
    const msg = errorPayload?.message;
    notify.error(msg && msg.length > 0 ? msg : 'You do not have permission to perform this action.', {
      autoClose: 30000,
    });
    return;
  }

  let showedToast = false;

  if (errorPayload?.validationErrors != null && errorPayload.validationErrors.length > 0) {
    const allLines: string[] = [];
    for (const validationError of errorPayload.validationErrors) {
      allLines.push(...splitErrorLines(validationError.message ?? ''));
    }
    const shouldIgnoreBatch = allLines.some(isSuppressed);
    if (!shouldIgnoreBatch && allLines.length > 0) {
      showedToast = toastErrorLines(allLines);
    }
  } else if (errorPayload?.message != null && errorPayload.message.length > 0) {
    if (!isSuppressed(errorPayload.message)) {
      const lines = splitErrorLines(errorPayload.message);
      if (lines.length > 1) {
        showedToast = toastErrorLines(lines);
      } else if (lines.length === 1) {
        notify.error(lines[0], { autoClose: 30000 });
        showedToast = true;
      }
    }
  }

  if (showedToast) return;

  const validationLines =
    errorPayload?.validationErrors?.flatMap((e) => splitErrorLines(e.message ?? '')) ?? [];
  const validationSuppressed =
    validationLines.length > 0 && validationLines.some(isSuppressed);
  const messageSuppressed =
    Boolean(errorPayload?.message) && isSuppressed(errorPayload!.message!);
  if (validationSuppressed || messageSuppressed) return;

  if (errorPayload != null || status != null) {
    showErrorModal({ status });
  } else if (status == null && errorPayload == null) {
    showErrorModal({ status: 0 });
  }
}
